import { PICKUP_ADDRESS_ID, WAREHOUSE_ADDRESS } from "~/constants/warehouse";
import { Address, SalesRequest } from "~/types/client";

/**
 * Checks if an address ID represents a pickup option
 */
export const isPickupAddress = (
  addressId: string | null | undefined
): boolean => {
  return addressId === PICKUP_ADDRESS_ID;
};

/**
 * Checks if a sales request is for pickup
 */
export const isPickupOrder = (salesRequest: SalesRequest): boolean => {
  // Check for PICKUP address ID or null address with warehouse complement
  return (
    isPickupAddress(salesRequest.deliveryAddressId) ||
    (salesRequest.deliveryAddressId === null &&
      salesRequest.deliveryComplement?.includes("Cliente retira en bodega"))
  );
};

/**
 * Formats an address for display, with special handling for pickup
 * NOTE: This function now relies on denormalized delivery fields in salesRequest.
 * If you need the current address data, use formatDeliveryAddressFromId instead.
 */
export const formatDeliveryAddress = (
  salesRequest: SalesRequest,
  options: {
    includePickupLabel?: boolean;
    short?: boolean;
  } = {}
): string => {
  const { includePickupLabel = true, short = false } = options;

  // Check if this is a pickup order
  if (isPickupOrder(salesRequest)) {
    const pickupText = includePickupLabel
      ? "🏢 Cliente retira en bodega: "
      : "";
    if (short) {
      return `${pickupText}${WAREHOUSE_ADDRESS.name}`;
    }
    return `${pickupText}${WAREHOUSE_ADDRESS.name} - ${WAREHOUSE_ADDRESS.street}, ${WAREHOUSE_ADDRESS.comuna}`;
  }

  // Regular delivery address using denormalized fields
  // WARNING: These fields may be stale if the address was updated after sales request creation
  const parts = [];

  // Combine street and number without comma
  const streetAndNumber = [
    salesRequest.deliveryStreet,
    salesRequest.deliveryNumber,
  ]
    .filter(Boolean)
    .join(" ");

  if (streetAndNumber) {
    parts.push(streetAndNumber);
  }

  if (salesRequest.deliveryComplement && !short) {
    parts.push(salesRequest.deliveryComplement);
  }

  if (salesRequest.deliveryComuna && !short) {
    parts.push(salesRequest.deliveryComuna);
  }

  if (salesRequest.deliveryCity) {
    parts.push(salesRequest.deliveryCity);
  }

  return parts.filter(Boolean).join(", ");
};

/**
 * Formats delivery address using the current address data (not denormalized fields)
 * This should be used when you want the most up-to-date address information
 */
export const formatDeliveryAddressFromAddress = (
  address: Address | null,
  isPickup: boolean = false,
  options: {
    includePickupLabel?: boolean;
    short?: boolean;
  } = {}
): string => {
  const { includePickupLabel = true, short = false } = options;

  // Check if this is a pickup order
  if (isPickup) {
    const pickupText = includePickupLabel
      ? "🏢 Cliente retira en bodega: "
      : "";
    if (short) {
      return `${pickupText}${WAREHOUSE_ADDRESS.name}`;
    }
    return `${pickupText}${WAREHOUSE_ADDRESS.name} - ${WAREHOUSE_ADDRESS.street}, ${WAREHOUSE_ADDRESS.comuna}`;
  }

  if (!address) {
    return "Sin dirección de entrega";
  }

  // Regular delivery address using current address data
  const parts = [];

  // Combine street and number without comma
  const streetAndNumber = [address.street, address.number]
    .filter(Boolean)
    .join(" ");

  if (streetAndNumber) {
    parts.push(streetAndNumber);
  }

  if (address.complement && !short) {
    parts.push(address.complement);
  }

  if (address.comuna && !short) {
    parts.push(address.comuna);
  }

  if (address.city) {
    parts.push(address.city);
  }

  return parts.filter(Boolean).join(", ");
};

/**
 * Gets the warehouse address information
 */
export const getWarehouseAddress = () => {
  return WAREHOUSE_ADDRESS;
};

/**
 * Creates a mock address object for pickup (used in address selection)
 */
export const createPickupAddress = (clientId: string = "pickup"): Address => {
  return {
    id: PICKUP_ADDRESS_ID,
    clientId,
    street: WAREHOUSE_ADDRESS.street,
    number: WAREHOUSE_ADDRESS.number,
    complement: `${WAREHOUSE_ADDRESS.name} - Pickup Location`,
    city: WAREHOUSE_ADDRESS.city,
    state: WAREHOUSE_ADDRESS.region,
    comuna: WAREHOUSE_ADDRESS.comuna,
    isDefault: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};
